// components/MonthNavigation.jsx

import React, { useMemo } from "react";
import {
  FaCalendarDay,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

import CalendarLegend from "./CalendarLegend";

import { formatMonthTitle } from "../utils/calendarUtils";

import {
  getMonthOptions,
  monthKeyToDate,
  toMonthKey,
} from "../utils/calendarViewUtils";

function NavigationButton({
  children,
  onClick,
  title,
  text = false,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={[
        "inline-flex h-10 items-center justify-center gap-2 rounded-xl border",
        "border-slate-200 bg-white font-bold text-slate-700",
        "transition hover:border-red-200 hover:bg-red-50 hover:text-red-700",
        "dark:border-white/10 dark:bg-slate-800 dark:text-slate-200",
        "dark:hover:bg-red-950/30 dark:hover:text-red-300",
        text ? "px-3 text-xs sm:text-sm" : "w-10",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

export default function MonthNavigation({
  monthDate,
  monthEvents = [],
  monthSourceEvents = [],

  legendEvents = [],
  hiddenPaletteKeys = [],

  onPreviousMonth,
  onNextMonth,
  onToday,
  onMonthChange,
  onTogglePalette,
}) {
  const monthOptions = useMemo(
    () => getMonthOptions(monthSourceEvents),
    [monthSourceEvents]
  );

  const currentMonthKey = toMonthKey(monthDate);

  const selectOptions = useMemo(() => {
    const currentExists = monthOptions.some(
      (option) =>
        option.value === currentMonthKey
    );

    if (currentExists) return monthOptions;

    return [
      {
        value: currentMonthKey,
        label: formatMonthTitle(monthDate),
        count: monthEvents.length,
      },
      ...monthOptions,
    ].sort((first, second) =>
      first.value.localeCompare(second.value)
    );
  }, [
    monthOptions,
    currentMonthKey,
    monthDate,
    monthEvents.length,
  ]);

  return (
    <div
      className={[
        "sticky top-[76px] z-30",
        "rounded-[1.5rem] border border-slate-200 bg-white/95 p-3",
        "shadow-[0_14px_35px_rgba(15,23,42,0.10)] backdrop-blur-xl",
        "dark:border-white/10 dark:bg-slate-900/95",
      ].join(" ")}
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="order-2 flex items-center gap-2 lg:order-1">
            <NavigationButton
              onClick={onPreviousMonth}
              title="Mois précédent"
            >
              <FaChevronLeft />
            </NavigationButton>

            <NavigationButton
              onClick={onToday}
              title="Revenir au mois actuel"
              text
            >
              <FaCalendarDay />
              Aujourd’hui
            </NavigationButton>

            <NavigationButton
              onClick={onNextMonth}
              title="Mois suivant"
            >
              <FaChevronRight />
            </NavigationButton>
          </div>

          <div className="order-1 min-w-0 lg:order-2 lg:text-center">
            <h2 className="text-xl font-black capitalize text-slate-900 dark:text-white md:text-2xl">
              {formatMonthTitle(monthDate)}
            </h2>

            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              {monthEvents.length} événement
              {monthEvents.length > 1 ? "s" : ""} sur
              ce mois
            </p>
          </div>

          <div className="order-3 lg:min-w-[260px]">
            <label className="mb-1 block text-[10px] font-black uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
              Accès rapide
            </label>

            <select
              value={currentMonthKey}
              onChange={(event) => {
                const date = monthKeyToDate(
                  event.target.value
                );

                if (date) {
                  onMonthChange?.(date);
                }
              }}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 outline-none focus:border-red-400 dark:border-white/10 dark:bg-slate-800 dark:text-slate-200"
            >
              {selectOptions.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                >
                  {option.label} — {option.count}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="border-t border-slate-200 pt-3 dark:border-white/10">
          <CalendarLegend
            events={legendEvents}
            hiddenPaletteKeys={
              hiddenPaletteKeys
            }
            onTogglePalette={onTogglePalette}
          />
        </div>
      </div>
    </div>
  );
}